/** Stage 39 · Pilot Programme Centre. */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, AlertTriangle, ClipboardCheck, MessageSquare, Plus } from "lucide-react";

const SEV = { critical: "bg-rose-100 text-rose-800 border-rose-200", high: "bg-orange-100 text-orange-800 border-orange-200", medium: "bg-amber-100 text-amber-800 border-amber-200", low: "bg-slate-100 text-slate-600 border-slate-200" };

export default function Pilot() {
  const [tab, setTab] = useState("pilots");
  return (
    <div className="max-w-6xl space-y-5" data-testid="pilot-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Administration · Pilot Programme</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Controlled Pilot Centre</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">Scope, gaps, feedback, readiness and sign-off — for every pilot customer. Critical gaps override the readiness score.</p>
      </div>
      <nav className="flex flex-wrap gap-1 border-b border-slate-200">
        {[{k:"pilots",l:"Pilots",I:Users},{k:"gaps",l:"Gaps",I:AlertTriangle},{k:"feedback",l:"Feedback",I:MessageSquare},{k:"readiness",l:"Readiness",I:ClipboardCheck}].map(({k,l,I})=>(
          <button key={k} onClick={()=>setTab(k)} data-testid={`pilot-tab-${k}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " + (tab===k ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <I className="h-4 w-4" /> {l}
          </button>
        ))}
      </nav>
      {tab === "pilots"    && <Pilots />}
      {tab === "gaps"      && <Gaps />}
      {tab === "feedback"  && <Feedback />}
      {tab === "readiness" && <Readiness />}
    </div>
  );
}

function Pilots() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/pilot/pilots").then(({data}) => setItems(data.items||[])), []);
  useEffect(()=>{ load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="pilot-pilots">
      <div className="flex justify-end"><button onClick={()=>setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="pilot-new"><Plus className="h-4 w-4" /> New pilot</button></div>
      {showForm && <PilotForm onCancel={()=>setShowForm(false)} onSaved={()=>{setShowForm(false); load();}} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left py-2 px-3">Pilot</th><th className="text-left py-2 px-3">Master Co</th><th className="text-left py-2 px-3">Scope</th><th className="text-left py-2 px-3">Owner</th><th className="text-left py-2 px-3">Status</th></tr></thead>
          <tbody>
            {items.length===0 ? <tr><td colSpan={5} className="py-6 text-center text-slate-500">No pilots yet.</td></tr>
              : items.map(p => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`pilot-row-${p.id}`}>
                  <td className="py-2 px-3 text-slate-900">{p.name}</td>
                  <td className="py-2 px-3 text-xs font-mono">{p.master_company_id?.slice(0,8)}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{(p.scope||[]).join(", ")}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{p.owner || "—"}</td>
                  <td className="py-2 px-3 text-xs">{p.status}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function PilotForm({onCancel, onSaved}) {
  const [f, setF] = useState({ master_company_id: "", name: "", scope: "assets,employees,deployment,recovery,shipments", countries: "IN", owner: "", status: "planning" });
  const save = async () => {
    try { await api.post("/pilot/pilots", { ...f, scope: f.scope.split(",").map(s=>s.trim()).filter(Boolean), countries: f.countries.split(",").map(s=>s.trim()).filter(Boolean) });
      toast.success("Pilot saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="pilot-form">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Name</div><input className="input" value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="pilot-name" /></label>
        <label><div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">master_company_id</div><input className="input" value={f.master_company_id} onChange={e=>setF({...f,master_company_id:e.target.value})} data-testid="pilot-mc" /></label>
        <label className="col-span-2"><div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Scope (comma-sep)</div><input className="input" value={f.scope} onChange={e=>setF({...f,scope:e.target.value})} /></label>
        <label><div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Countries</div><input className="input" value={f.countries} onChange={e=>setF({...f,countries:e.target.value})} /></label>
        <label><div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Owner</div><input className="input" value={f.owner} onChange={e=>setF({...f,owner:e.target.value})} /></label>
      </div>
      <div className="flex justify-end gap-2 pt-1"><button onClick={onCancel} className="h-9 px-3 text-sm border border-slate-200 rounded-md">Cancel</button><button onClick={save} className="h-9 px-3 text-sm bg-slate-900 text-white rounded-md" data-testid="pilot-save">Save</button></div>
    </div>
  );
}

function Gaps() {
  const [items, setItems] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [pilotId, setPilotId] = useState("");
  useEffect(()=>{ api.get("/pilot/pilots").then(({data})=>{ setPilots(data.items||[]); if((data.items||[])[0]) setPilotId(data.items[0].id); }); }, []);
  const load = useCallback(async () => {
    if (!pilotId) return setItems([]);
    const {data} = await api.get(`/pilot/pilots/${pilotId}`);
    setItems(data.gaps || []);
  }, [pilotId]);
  useEffect(()=>{ load(); }, [load]);
  const addGap = async () => {
    const title = window.prompt("Gap title?"); if (!title) return;
    const category = window.prompt("Category (missing_feature|ux|bug|integration|performance|permission|report|data|training|process|commercial|customer_configuration)?") || "bug";
    const severity = window.prompt("Severity (critical|high|medium|low)?") || "medium";
    try { await api.post("/pilot/gaps", { pilot_id: pilotId, title, category, severity }); toast.success("Gap added"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resolve = async (id) => { try { await api.post(`/pilot/gaps/${id}/resolve?resolution=fixed`); toast.success("Resolved"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-3" data-testid="pilot-gaps">
      <div className="flex items-end gap-2">
        <label className="flex-1"><div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Pilot</div><select className="input w-full" value={pilotId} onChange={e=>setPilotId(e.target.value)}>{pilots.map(p=> <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
        <button onClick={addGap} disabled={!pilotId} className="h-10 px-3 rounded-md bg-slate-900 text-white text-sm disabled:opacity-60" data-testid="pilot-gap-new">Add gap</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left py-2 px-3">Title</th><th className="text-left py-2 px-3">Category</th><th className="text-left py-2 px-3">Severity</th><th className="text-left py-2 px-3">Status</th><th className="text-right py-2 px-3"></th></tr></thead>
          <tbody>
            {items.length===0 ? <tr><td colSpan={5} className="py-6 text-center text-slate-500">No gaps recorded.</td></tr>
              : items.map(g => (
                <tr key={g.id} className="border-t border-slate-100">
                  <td className="py-2 px-3 text-slate-800">{g.title}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{g.category}</td>
                  <td className="py-2 px-3"><span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (SEV[g.severity] || SEV.low)}>{g.severity}</span></td>
                  <td className="py-2 px-3 text-xs">{g.status}</td>
                  <td className="py-2 px-3 text-right">{g.status==="open" && <button onClick={()=>resolve(g.id)} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">Resolve</button>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Feedback() {
  const [pilots, setPilots] = useState([]); const [pilotId, setPilotId] = useState(""); const [items, setItems] = useState([]);
  useEffect(()=>{ api.get("/pilot/pilots").then(({data})=>{ setPilots(data.items||[]); if((data.items||[])[0]) setPilotId(data.items[0].id); }); }, []);
  const load = useCallback(async () => { if (!pilotId) return; const {data} = await api.get(`/pilot/pilots/${pilotId}`); setItems(data.feedback||[]); }, [pilotId]);
  useEffect(()=>{ load(); }, [load]);
  const add = async () => {
    const ease = Number(window.prompt("Ease 1-5?") || 3);
    const email = window.prompt("Pilot user email?") || "user@example.com";
    try { await api.post("/pilot/feedback", { pilot_id: pilotId, user_email: email, ease }); toast.success("Recorded"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="pilot-feedback">
      <div className="flex items-end gap-2">
        <label className="flex-1"><div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Pilot</div><select className="input w-full" value={pilotId} onChange={e=>setPilotId(e.target.value)}>{pilots.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
        <button onClick={add} disabled={!pilotId} className="h-10 px-3 rounded-md bg-slate-900 text-white text-sm disabled:opacity-60">Capture feedback</button>
      </div>
      <ul className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-md">
        {items.length===0 ? <li className="p-6 text-sm text-slate-500 text-center">No feedback yet.</li>
          : items.map(f => <li key={f.id} className="p-3 text-sm"><b>Ease {f.ease}/5</b> · {f.user_email} · <span className="text-slate-500">{f.created_at?.slice(0,10)}</span></li>)}
      </ul>
    </div>
  );
}

function Readiness() {
  const [pilots, setPilots] = useState([]); const [pilotId, setPilotId] = useState(""); const [r, setR] = useState(null);
  useEffect(()=>{ api.get("/pilot/pilots").then(({data})=>{ setPilots(data.items||[]); if((data.items||[])[0]) setPilotId(data.items[0].id); }); }, []);
  useEffect(()=>{ if (!pilotId) return; api.get(`/pilot/pilots/${pilotId}/readiness`).then(({data})=>setR(data)).catch(()=>setR(null)); }, [pilotId]);
  return (
    <div className="space-y-3" data-testid="pilot-readiness">
      <label className="block max-w-md"><div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Pilot</div><select className="input w-full" value={pilotId} onChange={e=>setPilotId(e.target.value)}>{pilots.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
      {r && (
        <>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-center gap-3"><div className="text-3xl font-heading font-semibold text-slate-900 tabular-nums" data-testid="pilot-score">{r.overall_score}</div><div><div className="text-sm font-medium">{r.recommendation}</div><div className="text-xs text-slate-500 mt-1">{r.critical_open} critical · {r.high_open} high · {r.medium_open} medium open</div></div></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(r.dimensions||{}).map(([k,v])=><div key={k} className="rounded-md border border-slate-200 bg-white p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">{k.replace(/_/g," ")}</div><div className="mt-1 text-2xl font-heading font-semibold tabular-nums text-slate-900">{v}</div></div>)}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{r.note}</div>
        </>
      )}
    </div>
  );
}
